import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
  AxisLabelsFormatterContextObject,
  Chart,
  SeriesOptionsType,
  TooltipFormatterContextObject,
  XAxisPlotBandsOptions,
  YAxisOptions,
} from 'highcharts';
// eslint-disable-next-line import/named
import Highcharts, { StockChart } from 'highcharts/highstock';
highcharts(Highcharts);
import highcharts from 'highcharts/modules/map';
import { Observable, firstValueFrom, forkJoin, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import {
  check,
  isValidDate,
  programEventsCleaner,
  sleep,
  timeout,
} from '@helper';
import {
  // CostTimeseries,
  TariffCharts,
  CustomSeriesOptions,
  LoadProfileJson,
  LoadProfileStats,
  Scenario,
  ScenarioEvents,
} from '@model';
import {
  CookieService,
  ForecastService,
  LoadProfileService,
  LoaderService,
  ResultService,
  ScenarioEventsService,
  ScenarioService,
  SidenavService,
  TimezonesService,
} from '@service';
// import { snakeCase } from 'lodash'; // Commented out - no longer needed with TariffCharts
import { getBaseChartConfig } from './config/chart-base.config';
import { ChartService } from 'src/app/core/service/chart.service';

enum DOWNLOAD_TYPES {
  'ONE_ITERATION' = 0,
  'ALL_ITERATIONS' = 1,
}

@Component({
  selector: 'optima-multi-load-profile-chart',
  templateUrl: './multi-load-profile-chart.component.html',
  styleUrls: ['./multi-load-profile-chart.component.scss'],
})
export class MultiLoadProfileChartComponent implements OnInit, AfterViewInit, OnChanges {
  @Input() projectId: string;
  @Input() scenario: Scenario;
  @Input() beforeJson: LoadProfileJson[];
  @Input() beforeStats: LoadProfileStats;
  @Input() afterSolarJson: LoadProfileJson[];
  @Input() stateOfChargeJson: LoadProfileJson[];
  @Input() afterSolarStats: LoadProfileStats;
  afterEssJson: LoadProfileJson[];
  @Input() beforeColor: string;
  @Input() afterEssColor: string;
  @Input() afterSolarColor: string;
  @Input() stateOfChargeColor: string;
  @Input() chartId = 'multi-load-profile-container';
  @Input() mode: 'result' | 'form' | 'single';
  @Input() projectLabel: string;
  @Input() scenarioLabel: string;
  @Input() forecasted = false;
  @Input() showSecondaryChart = false;
  selectedGranularity: { unit: string; value: number };
  downloadDispatchProfileTypeForm: FormGroup;
  idx: string;
  adjustProfileId: string;
  chart: StockChart;
  secondaryChart: Chart;
  series: CustomSeriesOptions[] = [];
  private readonly translationCache = new Map<string, string>();
  dateFrom: Date;
  dateTo: Date;
  minDate: Date;
  maxDate: Date;
  locale: string;
  timezone: string;
  events: ScenarioEvents[];
  dpCreated = false;
  @ViewChild('datepickers') private templateRef: ElementRef<unknown>;

  constructor(
    private route: ActivatedRoute,
    private resultService: ResultService,
    public timezoneService: TimezonesService,
    public translateService: TranslateService,
    private sidenavService: SidenavService,
    private scenarioEventsService: ScenarioEventsService,
    private loadProfileService: LoadProfileService,
    private cookieService: CookieService,
    private loaderService: LoaderService,
    private forecastService: ForecastService,
    private chartService: ChartService,
    private scenarioService: ScenarioService,
  ) {
    this.sidenavService.toggle$.subscribe(() =>
      setTimeout(() => this.chart.reflow()),
    );

    this.downloadDispatchProfileTypeForm = new FormGroup({
      downloadDispatchProfileType: new FormControl(),
    });

    this.downloadDispatchProfileTypeForm.setValue({
      downloadDispatchProfileType: DOWNLOAD_TYPES.ONE_ITERATION,
    });
  }

  async ngAfterViewInit(): Promise<void> {
    this.loaderService.showLoaderNoHttp(`${this.chartId}-loader`);
    
    // Fallback timeout to hide loader if something goes wrong
    const fallbackTimeout = setTimeout(() => {
      console.warn('[MultiLoadProfileChart] Fallback: hiding loader after 10s timeout');
      this.loaderService.hideLoaderNoHttp(`${this.chartId}-loader`);
    }, 10000);

    setTimeout(async () => {
      try {
        if (this.mode !== 'result') {
          await this.setOptions();
        }
        this.datepickers();
        await this.coloredPlots();
        if (this?.chart?.xAxis) {
          setTimeout(() => {
            try {
              if (this.chart) {
                this.chart.redraw();
              }
              clearTimeout(fallbackTimeout);
              this.loaderService.hideLoaderNoHttp(`${this.chartId}-loader`);
            } catch (e) {
              console.error('[MultiLoadProfileChart] Error redrawing chart:', e);
              clearTimeout(fallbackTimeout);
              this.loaderService.hideLoaderNoHttp(`${this.chartId}-loader`);
            }
          });
        } else {
          clearTimeout(fallbackTimeout);
          this.loaderService.hideLoaderNoHttp(`${this.chartId}-loader`);
        }
      } catch (error) {
        console.error('[MultiLoadProfileChart] Error in ngAfterViewInit:', error);
        clearTimeout(fallbackTimeout);
        this.loaderService.hideLoaderNoHttp(`${this.chartId}-loader`);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['scenario'] && !changes['scenario'].firstChange) {
      this.selectedGranularity = undefined;
      
      if (this.chart) {
        this.chart.destroy();
        this.chart = null;
      }
      if (this.secondaryChart) {
        this.secondaryChart.destroy();
        this.secondaryChart = null;
      }
      
      setTimeout(async () => {
        this.loaderService.showLoaderNoHttp(`${this.chartId}-loader`);
        try {
          await this.setOptions();
        } catch (error) {
          console.error('[MultiLoadProfileChart] Error recreating charts on scenario change:', error);
        } finally {
          this.loaderService.hideLoaderNoHttp(`${this.chartId}-loader`);
        }
      }, 100);
    }
  }

  ngOnInit(): void {
    this.locale = this.timezoneService.adjustLocale(
      this.cookieService.getLocale(),
      false,
    );
    this.timezone = this.timezoneService.getTimezoneName(this.projectId);
    if (this.mode === 'result') {
      this.resultService.selectedResult$
        .pipe(
          switchMap(
            async (
              data: [
                Observable<LoadProfileJson[]>,
                string,
                Observable<LoadProfileJson[]>,
                boolean,
              ],
            ) => {
              while (!this.route.snapshot.queryParams['idx']) {
                await timeout(100);
              }
              this.idx = this.route.snapshot.queryParams['idx'];
              this.adjustProfileId = data[1];
              const loadProfileJson$ = data[0];
              const stateOfChargeJson$ = data[2];
              const events$ = this.scenarioEventsService.events$(
                this.scenario,
                this.idx,
                data[3],
              );
              return forkJoin([
                loadProfileJson$,
                events$,
                stateOfChargeJson$,
                of(data[3]),
              ]);
            },
          ),
        )
        .subscribe(
          // eslint-disable-next-line rxjs/no-async-subscribe
          async (
            data: Observable<
              [LoadProfileJson[], ScenarioEvents[], LoadProfileJson[], boolean]
            >,
          ) => {
            let toRedraw = false;
            const materializedData = await data.toPromise();
            if (
              !this.isSameLoadProfileData(this.stateOfChargeJson, materializedData[2])
            ) {
              toRedraw = true;
            }
            this.stateOfChargeJson = materializedData[2];
            if (!this.isSameScenarioEvents(this.events, materializedData[1])) {
              toRedraw = true;
            }
            this.events = materializedData[1];
            if (
              !this.scenario.options.energyStorage &&
              this.scenario.options.solarPv
            ) {
              if (
                !this.isSameLoadProfileData(this.afterSolarJson, materializedData[0])
              ) {
                toRedraw = true;
              }
              this.afterSolarJson = materializedData[0];
            } else {
              if (
                !this.isSameLoadProfileData(this.afterEssJson, materializedData[0])
              ) {
                toRedraw = true;
              }
              this.afterEssJson = materializedData[0];
            }
            if (toRedraw || materializedData[3]) {
              this.loaderService.showLoaderNoHttp(`${this.chartId}-loader`);
              setTimeout(async () => {
                await this.setOptions();
                this.loaderService.hideLoaderNoHttp(`${this.chartId}-loader`);
              });
            }
          },
        );
    }
  }

  async onGranularityChange(granularity: {
    unit: string;
    value: number;
  }): Promise<void> {
    this.selectedGranularity = granularity;
    const granularityValue = this.selectedGranularity?.value;

    if (!granularityValue) {
      return;
    }

    this.loaderService.showLoaderNoHttp(`${this.chartId}-loader`);

    try {
      const promises: Promise<void>[] = [];
      const { loadprofileId, adjustedPvLoadId } = this.scenario;
      const mainProfileId = this.adjustProfileId;

      if (this.beforeJson?.length > 0 && loadprofileId) {
        promises.push(
          firstValueFrom(
            this.loadProfileService.getLoadProfileJson(
              loadprofileId,
              granularityValue,
            ),
          ).then(data => {
            this.beforeJson = data;
          }),
        );
      }

      if (this.afterSolarJson?.length > 0 && adjustedPvLoadId) {
        promises.push(
          firstValueFrom(
            this.loadProfileService.getLoadProfileJson(
              adjustedPvLoadId,
              granularityValue,
            ),
          ).then(data => {
            this.afterSolarJson = data;
          }),
        );
      }

      if (this.afterEssJson?.length > 0 && mainProfileId) {
        promises.push(
          firstValueFrom(
            this.loadProfileService.getLoadProfileJson(
              mainProfileId,
              granularityValue,
            ),
          ).then(data => {
            this.afterEssJson = data;
          }),
        );
      }

      if (this.stateOfChargeJson?.length > 0 && mainProfileId) {
        promises.push(
          firstValueFrom(
            this.loadProfileService.loadSOC$(mainProfileId, granularityValue),
          ).then(data => {
            this.stateOfChargeJson = data;
          }),
        );
      }

      if (this.showSecondaryChart) {
        // OLD CODE - using costTimeseries
        // promises.push(
        //   firstValueFrom(
        //     this.scenarioService.getCostTimeseries$(this.scenario.id, granularityValue),
        //   ).then(response => {
        //     this.scenario.costTimeseries = response as any;
        //   }),
        // );
        
        // NEW CODE - using costTariffCharts
        promises.push(
          firstValueFrom(
            this.scenarioService.getCostTariffCharts$(this.scenario.id, granularityValue),
          ).then(response => {
            this.scenario.costTariffCharts = response as any;
          }),
        );
      }

      await Promise.all(promises);

      if (this.secondaryChart) {
        this.secondaryChart.destroy();
        this.secondaryChart = null;
      }

      await this.setOptions();
      if (this.chart) {
        this.chart.redraw();
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.loaderService.hideLoaderNoHttp(`${this.chartId}-loader`);
    }
  }

  private async setOptions(): Promise<void> {
    const series = await this.chartSeries();
    const secondarySeries = this.processSecondaryChartData();
    const plotBands = this.plotBands();
    const tooltipFormatter = this.tooltipFormatter;
    const redrawEvent = this.redraw;
    const displayDateTime = this.displayDateTime;

    const baseChartConfig = getBaseChartConfig({
      redrawEvent: redrawEvent,
      xAxisFormatter: this.xAxisFormatter,
      yAxis: this.getYAxis(this.series),
      plotBands: plotBands,
      displayDateTime: displayDateTime,
    });

    const pointInterval = this.selectedGranularity?.value 
      ? this.selectedGranularity.value * 60 * 1000
      : undefined;

    const chartExists = this.chart && this.chart.container && this.chart.series && this.chart.series.length >= 0;
    
    if (chartExists) {
      try {
        while (this.chart.series.length > 0) {
          this.chart.series[0].remove(false);
        }
        series.forEach(s => {
          this.chart.addSeries(s, false);
        });
        
        this.chart.xAxis[0].update({
          plotBands: plotBands,
        }, false);
        
        const yAxisConfig = this.getYAxis(this.series);
        if (Array.isArray(yAxisConfig)) {
          yAxisConfig.forEach((config, index) => {
            if (this.chart.yAxis[index]) {
              this.chart.yAxis[index].update(config, false);
            }
          });
        }
        
        this.chart.redraw();
      } catch (error) {
        console.warn('[MultiLoadProfileChart] Error updating chart, recreating:', error);
        if (this.chart) {
          this.chart.destroy();
        }
        this.chart = null;
        await this.setOptions();
        return;
      }
    } else {
      this.chart = Highcharts.chart(this.chartId, {
        ...baseChartConfig,
        plotOptions: {
          ...baseChartConfig.plotOptions,
          series: {
            ...(baseChartConfig.plotOptions?.series || {}),
            pointInterval: pointInterval,
          },
        },
      rangeSelector: {
        enabled: true,
        allButtonsEnabled: true,
        buttons: [
          {
            type: 'month',
            count: 1,
            text: this.translateService.instant(
              'chart.range_selector.buttons.1m',
            ),
          },
          {
            type: 'month',
            count: 3,
            text: this.translateService.instant(
              'chart.range_selector.buttons.3m',
            ),
          },
          {
            type: 'month',
            count: 6,
            text: this.translateService.instant(
              'chart.range_selector.buttons.6m',
            ),
          },
          {
            type: 'year',
            count: 1,
            text: this.translateService.instant(
              'chart.range_selector.buttons.1y',
            ),
          },
        ],
        selected: 4,
      },
      credits: {
        enabled: false,
      },
      series: series,
      tooltip: {
        shared: true,
        useHTML: true,
        shadow: false,
        borderColor: '#979797',
        backgroundColor: '#fff',
        formatter: function (): string {
          return tooltipFormatter(this);
        },
      },
      });
    }

    if (
      this.showSecondaryChart &&
      // OLD CODE - using costTimeseries
      // this.scenario?.costTimeseries?.length > 0 &&
      // NEW CODE - using costTariffCharts
      this.scenario?.costTariffCharts?.length > 0 &&
      secondarySeries &&
      secondarySeries.length > 0
    ) {
      const secondaryChartContainer = document.getElementById(`${this.chartId}-secondary`);
      if (!secondaryChartContainer) {
        console.warn(`[MultiLoadProfileChart] Secondary chart container not found: ${this.chartId}-secondary`);
        this.secondaryChart = null;
        return;
      }

      this.secondaryChart = Highcharts.chart(`${this.chartId}-secondary`, {
        ...baseChartConfig,
        plotOptions: {
          ...baseChartConfig.plotOptions,
          series: {
            ...(baseChartConfig.plotOptions?.series || {}),
            pointInterval: pointInterval,
          },
        },
        chart: {
          ...baseChartConfig.chart,
          zooming: {
            type: 'none' as Highcharts.OptionsChartZoomingTypeValue,
          },
        },
        series: secondarySeries,
        yAxis: {
          ...(baseChartConfig.yAxis as Highcharts.YAxisOptions),
          title: {
            text: this.translateService.instant('chart.price'),
            textAlign: 'center',
          },
          labels: {
            format: '{value} $/kWh',
          },
        },
        xAxis: {
          ...(baseChartConfig.xAxis as Highcharts.XAxisOptions),
          min: this.chart.xAxis[0].min ? this.chart.xAxis[0].min : undefined,
          max: this.chart.xAxis[0].max ? this.chart.xAxis[0].max : undefined,
          plotBands: [],
        },
        navigator: { enabled: false },
        scrollbar: { enabled: false },
        rangeSelector: { enabled: false },
        credits: { enabled: false },
        tooltip: {
          shared: true,
          useHTML: true,
          shadow: false,
          borderColor: '#979797',
          backgroundColor: '#fff',
          formatter: function (): string {
            return tooltipFormatter(this, secondarySeries);
          },
        },
      });

      Highcharts.addEvent(this.chart.xAxis[0], 'afterSetExtremes', (e: any) => {
        if (!e.trigger || !this.secondaryChart) return;
        
        const secondaryXAxis = this.secondaryChart.xAxis[0];
        if (secondaryXAxis && (secondaryXAxis.min !== e.min || secondaryXAxis.max !== e.max)) {
          secondaryXAxis.setExtremes(e.min, e.max, true, false);
        }
      });

      if (this.chart && this.secondaryChart) {
        const granularityMinutes = this.selectedGranularity?.value || 15;
        this.chartService.setPointIntervalThreshold(granularityMinutes);
        
        this.chartService.synchronizeCharts(
          [this.chart, this.secondaryChart],
          this.chartService.handleSyncEvent.bind(this.chartService),
        );
      }
      
      const mainMin = this.chart.xAxis[0].min;
      const mainMax = this.chart.xAxis[0].max;
      if (mainMin !== undefined && mainMax !== undefined) {
        this.secondaryChart.xAxis[0].setExtremes(mainMin, mainMax, false, false);
        this.secondaryChart.redraw();
      }
    } else {
      this.secondaryChart = null;
    }
  }

  private getYAxis(series: CustomSeriesOptions[]): YAxisOptions[] {
    const yAxis: YAxisOptions[] = [
      {
        title: {
          text: this.translateService.instant('chart.demand'),
          textAlign: 'center',
        },
        labels: {
          format: '{value} kW',
        },
        showLastLabel: true,
        showFirstLabel: true,
        visible: true,
        startOnTick: false,
        endOnTick: true,
        maxPadding: 0,
        minPadding: 0,
        opposite: false,
        alignTicks: false,
        softMin: 0,
      },
    ];
    const socSeries = series.find((serie: CustomSeriesOptions) => {
      return serie.name === 'State of Charge (SOC)';
    });
    if (socSeries) {
      yAxis.push({
        title: {
          text: this.translateService.instant('chart.soc_percent'),
          textAlign: 'center',
        },
        labels: {
          format: '{value} %',
        },
        showEmpty: false,
        showLastLabel: true,
        gridLineWidth: 0,
        visible: true,
        startOnTick: false,
        endOnTick: true,
        maxPadding: 0,
        minPadding: 0,
        opposite: true,
        tickInterval: 25,
        tickAmount: 5,
      });
    }
    return yAxis;
  }

  xAxisFormatter = (data: AxisLabelsFormatterContextObject): string => {
    return this.displayDateTime(data.value, 'Do MMMM YYYY h:mm a');
  };

  tooltipFormatter = (
    data: TooltipFormatterContextObject,
    series?: SeriesOptionsType[],
  ): string => {
    const dateHtml = this.displayDateTime(data.x, 'Do MMMM YYYY h:mm a z');
    const containerStyle = 'width: fit-content; background-color: #fff';
    const dateStyle = 'font-size: 13px; margin-bottom: 0.5rem; display: block';
    const h2Style = `font-size: 15px; margin-right: 0.5rem; display: inline-block;`;
    const kwStyle = `font-size: 15px; display: inline-block; font-weight: bold`;
    let tooltipContent = '';
    const tooltipSeries = series ?? this.series;
    tooltipSeries.forEach(option => {
      const points = data.points.find(
        series => series.series.name === option.name,
      );
      const containerDisplay = points ? 'block' : 'none';
      const divStyle = `display: inline-block; width: 0.75rem; height: 0.75rem; background: ${option.color}; border-radius: 50%; margin-right: 0.25rem`;
      tooltipContent += `
        <div style="display: ${containerDisplay}">
          <div style="${divStyle}"></div>
          <span style="${h2Style}">
            ${this.translationCache.get(option.translationKey) ?? this.translateService.instant(option.translationKey)} |
          </span>
          <span style="${kwStyle}">${points?.y.toFixed(2)} ${
            option.tooltipUnit
          }</span>
        </div>
      `;
    });
    return `
      <div style="${containerStyle}">
        <span style="${dateStyle}" data-automation="highchart-tooltip">${dateHtml}</span>
        ${tooltipContent}
      </div>
    `;
  };

  redraw = (): void => {
    if (!this.chart?.xAxis) {
      this.setDatepicker();
    }
    this.datepickers();
    setTimeout(async () => await this.coloredPlots());
  };

  private async chartSeries(): Promise<SeriesOptionsType[]> {
    let series: CustomSeriesOptions[] = [];
    if (this.mode === 'result') {
      if (this.afterSolarJson?.length > 0 && !this.afterEssJson) {
        const opt1TranslationKey = 'scenario.results.before_solar';
        const opt1: CustomSeriesOptions = {
          color: this.beforeColor,
          json: this.beforeJson,
          translationKey: opt1TranslationKey,
          tooltipUnit: 'kW',
          name: this.translateService.instant(opt1TranslationKey),
          visible: true,
        };
        const opt2TranslationKey = 'scenario.results.after_solar';
        const opt2: CustomSeriesOptions = {
          color: this.afterSolarColor,
          json: this.afterSolarJson,
          translationKey: opt2TranslationKey,
          tooltipUnit: 'kW',
          name: this.translateService.instant(opt2TranslationKey),
          visible: true,
        };
        series = [opt1, opt2];
      } else {
        const opt1TranslationKey = 'scenario.results.before_ess';
        const opt1: CustomSeriesOptions = {
          color: this.beforeColor,
          json: this.beforeJson,
          translationKey: opt1TranslationKey,
          tooltipUnit: 'kW',
          name: this.translateService.instant(opt1TranslationKey),
          visible: true,
        };
        const opt2TranslationKey = 'scenario.results.after_ess';
        const opt2: CustomSeriesOptions = {
          color: this.afterEssColor,
          json: this.afterEssJson,
          translationKey: opt2TranslationKey,
          tooltipUnit: 'kW',
          name: this.translateService.instant(opt2TranslationKey),
          visible: true,
        };

        series = [opt1, opt2];
      }

      if (
        this.beforeJson?.length > 0 &&
        this.afterEssJson?.length > 0 &&
        this.afterSolarJson?.length > 0
      ) {
        const opt1TranslationKey =
          'scenario.results.chart.before_solar_and_ess';
        const opt1: CustomSeriesOptions = {
          color: this.beforeColor,
          json: this.beforeJson,
          translationKey: opt1TranslationKey,
          name: this.translateService.instant(opt1TranslationKey),
          yAxis: 0,
          tooltipUnit: 'kW',
          getExtremesFromAll: true,
          visible: true,
        };
        const opt2TranslationKey =
          'scenario.results.chart.after_solar_before_ess';
        const opt2: CustomSeriesOptions = {
          color: this.afterSolarColor,
          json: this.afterSolarJson,
          translationKey: opt2TranslationKey,
          name: this.translateService.instant(opt2TranslationKey),
          yAxis: 0,
          tooltipUnit: 'kW',
          getExtremesFromAll: true,
          visible: true,
        };
        const opt3TranslationKey = 'scenario.results.chart.after_ess_and_solar';
        const opt3: CustomSeriesOptions = {
          color: this.afterEssColor,
          json: this.afterEssJson,
          translationKey: opt3TranslationKey,
          name: this.translateService.instant(opt3TranslationKey),
          yAxis: 0,
          tooltipUnit: 'kW',
          getExtremesFromAll: true,
          visible: true,
        };
        series = [opt1, opt2, opt3];
      }

      if (this.stateOfChargeJson?.length > 0) {
        const stateOfChargeOptionTranslationKey =
          'scenario.results.chart.state_of_charge';
        const stateOfChargeOption: CustomSeriesOptions = {
          color: this.stateOfChargeColor,
          json: this.stateOfChargeJson,
          translationKey: stateOfChargeOptionTranslationKey,
          name: this.translateService.instant(
            stateOfChargeOptionTranslationKey,
          ),
          yAxis: 1,
          tooltipUnit: '%',
          getExtremesFromAll: true,
          visible: false,
        };

        series.push(stateOfChargeOption);
      }

      return await this.renderChart(series);
    } else {
      const opt1TranslationKey = 'scenario.results.before_solar';
      const opt1: CustomSeriesOptions = {
        color: this.beforeColor,
        json: this.beforeJson,
        translationKey: opt1TranslationKey,
        tooltipUnit: 'kW',
        name: this.translateService.instant(opt1TranslationKey),
        visible: true,
      };
      const opt2TranslationKey = 'scenario.results.after_solar';
      const opt2: CustomSeriesOptions = {
        color: this.afterSolarColor,
        json: this.afterSolarJson,
        translationKey: opt2TranslationKey,
        tooltipUnit: 'kW',
        name: this.translateService.instant(opt2TranslationKey),
        visible: true,
      };
      series = [opt1];
      if (this.mode !== 'single') {
        series.push(opt2);
      }

      return await this.renderChart(series);
    }
  }
  

  private processSecondaryChartData(): SeriesOptionsType[] {
    if (!this.showSecondaryChart) {
      return null;
    }

    // OLD CODE - using costTimeseries
    // if (this.scenario?.costTimeseries && this.scenario.costTimeseries.length > 0) {
    //   const highchartsColors = Highcharts.getOptions().colors;

    //   const series = this.scenario.costTimeseries.map((item, index) => {
    //     const rawData: [number, number][] = item.data.map(point => {
    //       const timestamp = point.timestamp < 10000000000 
    //         ? point.timestamp * 1000
    //         : point.timestamp;
    //       const rate = Number(point.value);
    //       return [timestamp, rate];
    //     });

    //     const getCostTimeseriesLabel = (costItem: CostTimeseries): string => {
    //       const base = snakeCase(String(costItem?.type ?? ''));
    //       return costItem?.dynamic ? `${base}_dynamic` : `${base}_static`;
    //     };

    //     const translationKey = `scenario.results.chart.${getCostTimeseriesLabel(item)}`;
    //     const translatedName = this.translateService.instant(translationKey);

    //     const serie: SeriesOptionsType = {
    //       name: translatedName,
    //       id: item.id,
    //       data: rawData,
    //       type: 'line' as const,
    //       yAxis: 0,
    //       translationKey: translationKey,
    //       tooltipUnit: '$',
    //       color: highchartsColors[index % highchartsColors.length],
    //     } as SeriesOptionsType;
    //     return serie;
    //   });
    //   
    //   return series;
    // }

    // NEW CODE - using costTariffCharts
    if (this.scenario?.costTariffCharts && this.scenario.costTariffCharts.length > 0) {
      const highchartsColors = Highcharts.getOptions().colors;

      const series = this.scenario.costTariffCharts.map((item, index) => {
        const rawData: [number, number][] = item.timeSeries.map(point => {
          const timestamp = point.timestamp < 10000000000 
            ? point.timestamp * 1000
            : point.timestamp;
          const rate = Number(point.value);
          return [timestamp, rate];
        });

        const getTariffChartsLabel = (tariffItem: TariffCharts): string => {
          let type = 'after_tariff';
          if (tariffItem.before) {
            type = 'before_tariff';
          }
          const prefix = tariffItem.importRate ? 'import_' : 'export_';
          type = `${prefix}${type}`;
          return tariffItem.dynamic ? `${type}_dynamic` : `${type}_static`;
        };
        const translationKey = `scenario.results.chart.${getTariffChartsLabel(item)}`;
        const translatedName = this.translateService.instant(translationKey);

        const serie: SeriesOptionsType = {
          name: translatedName,
          id: item.id,
          data: rawData,
          type: 'line' as const,
          yAxis: 0,
          translationKey: translationKey,
          tooltipUnit: '$/kWh',
          color: highchartsColors[index % highchartsColors.length],
        } as SeriesOptionsType;
        return serie;
      });
      
      return series;
    }

    return null;
  }

  private async renderChart(
    options: CustomSeriesOptions[],
  ): Promise<SeriesOptionsType[]> {
    const series: SeriesOptionsType[] = [];
    this.series = [];
    const precomputedChartData = await this.adjustDateWithWorker(options);
    this.maxDate = this.adjustedDatepickerDate(this.maxDate, true);
    this.minDate = this.adjustedDatepickerDate(this.minDate, true);
    options.forEach((option, index) => {
      option.active = true;
      this.series.push(option);
      if (option.translationKey && !this.translationCache.has(option.translationKey)) {
        this.translationCache.set(
          option.translationKey,
          this.translateService.instant(option.translationKey),
        );
      }
      series.push({
        name: option.name,
        type: 'line',
        showInLegend: true,
        data: precomputedChartData[index] ?? [],
        color: option.color,
        showInNavigator: true,
        yAxis: option.yAxis,
        visible: option.visible,
      });
      const id = `chart-style-${option.name}`;
      const previousStyle = document.getElementById(id);
      if (previousStyle) {
        previousStyle.remove();
      }
      const style = document.createElement('style');
      style.id = id;
      const styleElem = document.head.appendChild(style);
      styleElem.innerHTML = `
      .checkbox-container .checkmark.${option.name}::after {
        border: solid ${option.color};
        border-width: 0 1px 1px 0;
      }`;
    });
    return series;
  }

  closedPicker(): void {
    this.changeGraphDates();
  }

  private createDatepicker(): void {
    document
      .getElementById(this.chartId)
      .appendChild(this.templateRef.nativeElement as HTMLDivElement);
  }

  private setDatepicker = (): void => {
    if (!this.chart?.xAxis) {
      setTimeout(() => this.setDatepicker(), 100);
      return;
    }
    this.dateFrom = new Date(this.chart.xAxis[0].min);
    this.dateTo = new Date(this.chart.xAxis[0].max);
  };

  private moveDatepickers(): void {
    const newParent = document
      .getElementById(this.chartId)
      .getElementsByClassName('highcharts-input-group')
      .item(0) as SVGGElement;
    const datePickers = document
      .getElementById(this.chartId)
      .querySelector('#datepickers') as HTMLDivElement;
    if (!datePickers) {
      this.datepickers();
      return;
    }
    if (!newParent) {
      setTimeout(() => this.moveDatepickers(), 100);
      return;
    }
    if (!newParent.firstChild) {
      return;
    }
    while (newParent.firstChild) {
      newParent.removeChild(newParent.firstChild);
    }
    const foreignObject = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'foreignObject',
    );
    foreignObject.setAttribute('transform', 'translate(0,0)');
    foreignObject.setAttribute('width', String(250));
    foreignObject.setAttribute('height', String(50));
    foreignObject.appendChild(datePickers);
    newParent.appendChild(foreignObject);
  }

  private datepickers(): void {
    this.dpCreated = false;
    this.createDatepicker();
    this.setDatepicker();
    this.moveDatepickers();
    this.dpCreated = true;
  }

  changeGraphDates(): void {
    const xAxis = this.chart.xAxis[0];
    xAxis.setExtremes(this.dateFrom.valueOf(), this.dateTo.valueOf());
    if (this.secondaryChart) {
      const secondaryXAxis = this.secondaryChart.xAxis[0];
      secondaryXAxis.setExtremes(
        this.dateFrom.valueOf(),
        this.dateTo.valueOf(),
      );
    }
    // setTimeout(async () => await this.coloredPlots(), 500);
  }

  adjustedDatepickerDate(date: Date, adjustForMinMax = false): Date {
    const userTimezoneOffset = date.getTimezoneOffset() * 60000;
    const isTimezone = !!this.timezoneService.getTimezoneName(this.projectId);
    let offset: number;
    if (isTimezone) {
      offset = this.timezoneService.getDateTimezoneOffset(
        this.projectId,
        date.toISOString(),
      );
    } else {
      offset = -(date.getTimezoneOffset() * 60000);
    }
    if (adjustForMinMax) {
      return new Date(date.getTime() + offset + userTimezoneOffset);
    } else {
      return new Date(date.getTime() - offset - userTimezoneOffset);
    }
  }

  sanityCheckDate(date: Date): Date {
    if (!date) {
      return undefined;
    }
    return new Date(date.getTime());
  }

  private plotBands(): XAxisPlotBandsOptions[] {
    if (!this.events) {
      return [];
    }
    const allEvents = this.events;
    const uniqueEvents = allEvents.filter(
      (event, index, self) =>
        index ===
        self.findIndex(
          ev =>
            ev.programName === event.programName &&
            ev.startEvent === event.startEvent &&
            ev.endEvent === event.endEvent,
        ),
    );
    return uniqueEvents.map(event => {
      const id = programEventsCleaner(`${event.programName}${event.programId}`);
      const className = programEventsCleaner(`${event.programName}`);
      return {
        color: '#D9D9D980',
        id,
        from: +this.sanityCheckDate(new Date(event.startEvent)),
        to: +this.sanityCheckDate(new Date(event.endEvent)),
        className,
      };
    });
  }

  eventsProgramsDistinct(): string[] {
    const events = this.events;
    return [...new Set(events.map(event => event.programName))];
  }

  toggleAllEvents(): void {
    const plots = this.chart.xAxis[0]['plotLinesAndBands'];
    if (plots?.length) {
      plots.forEach(plot => {
        this.chart.xAxis[0].removePlotBand(plot.id);
      });
      if (plots.length) {
        this.toggleAllEvents();
      }
    } else {
      this.plotBands().forEach(plot => {
        this.chart.xAxis[0].addPlotBand(plot);
      });
    }
    setTimeout(async () => await this.coloredPlots());
  }

  toggleEvent(programName: string): void {
    programName = programEventsCleaner(programName);
    const plots = this.chart.xAxis[0]['plotLinesAndBands'];
    if (plots?.some(plot => plot.id.includes(programName))) {
      plots
        .filter(plot => plot.id.includes(programName))
        .forEach(plot => {
          this.chart.xAxis[0].removePlotBand(plot.id);
        });
    } else {
      this.plotBands()
        .filter(plot => plot.id.includes(programName))
        .forEach(plot => {
          this.chart.xAxis[0].addPlotBand(plot);
        });
    }
    setTimeout(async () => await this.coloredPlots());
  }

  isPlotShow(programName: string): boolean {
    programName = programEventsCleaner(programName);
    return this.chart.xAxis[0]['plotLinesAndBands'].some(plot =>
      plot.id.includes(programName),
    );
  }

  areAllProgramsHidden(): boolean {
    return !!!this.chart.xAxis[0]['plotLinesAndBands']?.length;
  }

  private coloredPlots = async (): Promise<void> => {
    if (this.mode !== 'result') {
      return;
    }
    const borderElements = Array.from(
      document.querySelectorAll('.colored-border'),
    ) as SVGPathElement[];
    borderElements.forEach(element => element.remove());
    if (!this.events) {
      await sleep(1000);
      await this.coloredPlots();
    }
    const allPaths = this.eventsProgramsDistinct().flatMap(eventName =>
      Array.from(
        document.querySelectorAll(`.${programEventsCleaner(eventName)}`),
      ),
    ) as SVGPathElement[];
    const style = getComputedStyle(document.body);
    allPaths.forEach(
      path => (path.id = (Math.random() * (1000000 - 1) + 1).toString()),
    );
    allPaths.forEach(path => {
      const eventIndex = this.eventsProgramsDistinct()
        .map(eventName => programEventsCleaner(eventName))
        .indexOf(path.classList[path.classList.length - 1]);
      const borderElement = path.cloneNode() as SVGPathElement;
      borderElement.setAttribute(
        'fill',
        style.getPropertyValue(`--color-${eventIndex + 1}`),
      );
      const d = borderElement.getAttribute('d');
      const splittedD = d.split(' ');
      const start = splittedD[1];
      const end = splittedD[7];
      const overlapping = allPaths.filter(spath => {
        if (path.id === spath.id || !spath.id) {
          return false;
        }
        const spSplitted = spath.getAttribute('d').split(' ');
        const spStart = spSplitted[1];
        const spEnd = spSplitted[7];
        return (
          (start > spStart && start < spEnd) ||
          (end > spStart && end < spEnd) ||
          (start <= spStart && end >= spEnd)
        );
      });
      splittedD[2] = (73 - overlapping.length * 5).toString();
      splittedD[5] = (68 - overlapping.length * 5).toString();
      splittedD[8] = (68 - overlapping.length * 5).toString();
      splittedD[11] = (73 - overlapping.length * 5).toString();
      borderElement.setAttribute('d', splittedD.join(' '));
      borderElement.setAttribute('class', 'colored-border');
      path.after(borderElement);
      path.removeAttribute('id');
    });
  };

  downloadDispatchProfile(): void {
    const forecastLabel = !this.forecastService.isThereForecasted(this.scenario)
      ? ''
      : this.forecasted
        ? 'forecasted_'
        : 'historical_';

    const downloadDispatchProfileType =
      this.downloadDispatchProfileTypeForm.get(
        'downloadDispatchProfileType',
      ).value;

    if (downloadDispatchProfileType === DOWNLOAD_TYPES.ONE_ITERATION) {
      const profileId = this.adjustProfileId || this.scenario.loadprofileId;
      if (!profileId) {
        return;
      }
      this.loadProfileService
        .downloadDispatchProfileCsv(
          profileId,
          this.projectLabel,
          this.scenarioLabel,
          this.idx,
          this.timezoneService.getDateTimeDisplayLabel(
            this.projectId,
            Date(),
            'll',
          ),
          this.timezoneService.getTimezoneName(this.projectId),
          forecastLabel,
          null,
          this.selectedGranularity?.value,
        )
        .subscribe(data => {
          const link = document.createElement('a');
          const blob = new Blob([data.csv], {
            type: 'text/csv;charset=UTF-8',
          });
          link.href = URL.createObjectURL(blob);
          link.download = data.filename;
          link.click();
        });
    } else if (downloadDispatchProfileType === DOWNLOAD_TYPES.ALL_ITERATIONS) {
      this.loadProfileService.downloadAllDispatchProfileCsv(
        this.projectLabel,
        this.scenario,
        this.idx,
        this.timezoneService.getDateTimeDisplayLabel(
          this.projectId,
          Date(),
          'll',
        ),
        this.timezoneService.getTimezoneName(this.projectId),
        forecastLabel,
        this.forecasted,
        this.selectedGranularity?.value,
      );
    }
  }

  addCssForSelect(): void {
    const style = document.createElement('style');
    style.id = 'custom-style';
    style.innerText =
      '.mat-select-panel {' + 'max-width: unset !important' + '}';
    document.head.appendChild(style);
  }

  removeCssForSelect(): void {
    document.getElementById('custom-style').remove();
  }

  displayDateTime = (data: number | string, dateTimeFormat: string): string => {
    return this.timezoneService.getDateTimeDisplayLabel(
      this.projectId,
      data as string,
      dateTimeFormat,
      true,
      true,
    );
  };

  private async adjustDateWithWorker(
    options: CustomSeriesOptions[],
  ): Promise<Array<[number, number][]>> {
    if (typeof Worker !== 'undefined') {
      const worker = new Worker(new URL('./chart.worker', import.meta.url));
      const seriesData = options.map(opt => ({
        timestamps: opt.json?.map(p => p.timestamp) ?? [],
        values: opt.json?.map(p => p.value) ?? [],
      }));
      return new Promise((res, rej) => {
        worker.onerror = (): void => { worker.terminate(); rej(new Error('Chart worker error')); };
        worker.onmessage = ({ data }): void => {
          this.minDate = data['minDate'];
          this.maxDate = data['maxDate'];
          worker.terminate();
          res(data['chartData']);
        };
        worker.postMessage({ seriesData, timezone: this.timezone, locale: this.locale });
      });
    } else {
      const chartData: Array<[number, number][]> = options.map(opt => {
        if (!opt.json) return [];
        return opt.json.map(json => {
          const d = new Date(json.timestamp);
          let adjustedDate = this.sanityCheckDate(
            new Date(d.toLocaleString(this.locale, { timeZone: this.timezone })),
          );
          if (!isValidDate(adjustedDate)) {
            adjustedDate = this.sanityCheckDate(
              new Date(d.toLocaleString('en-US', { timeZone: this.timezone })),
            );
          }
          this.minDate ??= adjustedDate;
          this.maxDate ??= adjustedDate;
          if (this.minDate > adjustedDate) this.minDate = adjustedDate;
          if (this.maxDate < adjustedDate) this.maxDate = adjustedDate;
          return [+d, json.value] as [number, number];
        });
      });
      return chartData;
    }
  }

  private isSameLoadProfileData(
    a: LoadProfileJson[] | null | undefined,
    b: LoadProfileJson[] | null | undefined,
  ): boolean {
    if (a === b) return true;
    if (!a || !b) return false;
    if (a.length !== b.length) return false;
    if (a.length === 0) return true;
    return a[0].timestamp === b[0].timestamp &&
      a.at(-1).timestamp === b.at(-1).timestamp;
  }

  private isSameScenarioEvents(
    a: ScenarioEvents[] | null | undefined,
    b: ScenarioEvents[] | null | undefined,
  ): boolean {
    if (a === b) return true;
    if (!a || !b) return false;
    if (a.length !== b.length) return false;
    if (a.length === 0) return true;
    return a[0].startEvent === b[0].startEvent &&
      a.at(-1).endEvent === b.at(-1).endEvent;
  }

  granularityOptions = [
    {
      label: 'scenario.results.granularity_15_min',
      value: { unit: 'minute', value: 15 },
    },
    {
      label: 'scenario.results.granularity_30_min',
      value: { unit: 'minute', value: 30 },
    },
    {
      label: 'scenario.results.granularity_1_hour',
      value: { unit: 'minute', value: 60 },
    },
  ];

  protected readonly check = check;
  protected readonly DOWNLOAD_TYPES = DOWNLOAD_TYPES;
}
